/**
 * 事务管理器Hook - 封装所有对核心事务数据的操作和业务逻辑
 * 
 * 职责：
 * - 管理事务数据的CRUD操作
 * - 处理明细的添加、更新、删除
 * - 构建批量处理请求payload
 * - 检查提交条件
 * - 获取变更统计
 * - 提交事务
 */

import { useCallback } from 'react';
import { message } from 'antd';
import { CLIENT_STATUS, TRANSACTION_STATUS } from '../types';
import { PERMIT_TYPES } from '../../Monitor/constants/transactionConstants';
import { useBatchTransactionAPI } from './useBatchTransactionAPI';

// 从合同项的tags确定许可类型
const determinePermitTypeFromTags = (tags) => {
  if (!tags || !Array.isArray(tags)) {
    return PERMIT_TYPES.OVERFLIGHT; // 默认为飞越许可
  }

  // 优先检查新的标签
  if (tags.includes("OVERFLIGHT")) {
    return PERMIT_TYPES.OVERFLIGHT;
  } else if (tags.includes("LANDING_TAKEOFF")) {
    return PERMIT_TYPES.LANDING_TAKEOFF;
  }
  // 兼容旧的标签 - 起飞和落地都归类为起降许可
  else if (tags.includes("DEPARTURE") || tags.includes("ARRIVAL")) {
    return PERMIT_TYPES.LANDING_TAKEOFF;
  } else {
    // 默认为飞越许可
    return PERMIT_TYPES.OVERFLIGHT;
  }
};

export const useTransactionManager = (
  transactions, 
  selectedFlights, // <-- 鎺ユ敹 selectedFlights
  transactionType, 
  workflowMode,
  selectedSupplier,
  activeAirports,
  dispatch,
  actionCreators
) => {
  const { batchProcessTransactions } = useBatchTransactionAPI();

  // 添加明细（增强版 - 自动处理许可类型）
  const addDetailWithPermitType = useCallback((transactionId, contractItem) => {
    console.log("🔍 addDetailWithPermitType 被调用:", {
      transactionId,
      contractItem,
      transactionType,
      actionCreators: !!actionCreators,
      dispatch: !!dispatch
    });

    // 从合同项tags确定许可类型
    const permitType = transactionType === "PERMIT_REQUEST" ? determinePermitTypeFromTags(contractItem.tags) : null;

    console.log("🔍 手动添加明细 - 许可类型确定:", {
      contractItemId: contractItem.id,
      tags: contractItem.tags,
      permitType: permitType,
      transactionType: transactionType,
    });

    const newDetail = {
      contractItemId: contractItem.id,
      productName: contractItem.product_name || contractItem.name || "",
      supplierName: contractItem.partner_name || contractItem.supplier_name || "",
      unitPrice: contractItem.price || 0,
      currency: contractItem.currency || "CNY",
      quantity: 1,
      unit: contractItem.product_unit || contractItem.unit || "",
      currentStatus: TRANSACTION_STATUS.PENDING,
      targetStatus: null,
      remarks: null,
      // 添加许可类型信息
      permitType: permitType,
      typeSpecificFields: permitType
        ? {
            permitType: permitType,
            // 根据许可类型设置默认值
            ...(permitType === PERMIT_TYPES.OVERFLIGHT
              ? {
                  overflightCountriesCount: 1,
                  overflightApprovedCount: 0,
                  overflightCountries: [
                    {
                      country_code: "UNKNOWN",
                      country_name: "待确定",
                      status: "PENDING",
                      reference_number: "",
                      remarks: "",
                    },
                  ],
                }
              : {}),
            ...(permitType === PERMIT_TYPES.LANDING_TAKEOFF
              ? {
                  airportCode: contractItem.applicable_airports?.[0] || null,
                  airportSlotStart: null,
                  airportSlotEnd: null,
                }
              : {}),
          }
        : {},
    };

    console.log("🔍 准备dispatch addDetail:", {
      transactionId,
      newDetail,
      actionCreators: actionCreators
    });

    try {
      dispatch(actionCreators.addDetail(transactionId, newDetail));
      console.log("✅ addDetail dispatch 成功");
    } catch (error) {
      console.error("❌ addDetail dispatch 失败:", error);
    }
  }, [transactionType, dispatch, actionCreators]);

  // 更新明细
  const updateDetail = useCallback((transactionId, detailId, updates) => {
    dispatch(actionCreators.updateDetail(transactionId, detailId, updates));
  }, [dispatch, actionCreators]);

  // 删除明细
  const deleteDetail = useCallback((transactionId, detailId) => {
    dispatch(actionCreators.deleteDetail(transactionId, detailId));
  }, [dispatch, actionCreators]);

  // 恢复明细
  const restoreDetail = useCallback((transactionId, detailId) => {
    dispatch(actionCreators.restoreDetail(transactionId, detailId));
  }, [dispatch, actionCreators]);

  // 重置明细
  const resetDetail = useCallback((transactionId, detailId) => {
    dispatch(actionCreators.resetDetail(transactionId, detailId));
  }, [dispatch, actionCreators]);

  // 更新事务
  const updateTransaction = useCallback((transactionId, updates) => {
    dispatch(actionCreators.updateTransaction(transactionId, updates));
  }, [dispatch, actionCreators]);

  // 批量创建事务壳
  const createTransactionShells = useCallback((flights) => {
      dispatch(actionCreators.addTransactionShells(flights));
  }, [dispatch, actionCreators]);

  // 检查是否有变更
  const hasChanges = useCallback(() => {
    return transactions.some((transaction) => {
      // 检查明细是否有变更
      const hasDetailChanges = transaction.details.some((detail) => detail.clientStatus !== CLIENT_STATUS.PRISTINE);

      // 检查事务本身是否有变更
      const hasTransactionChanges = typeof transaction.id === "number" && transaction.originalData && 
        (transaction.status !== transaction.originalData.status || transaction.remarks !== transaction.originalData.remarks);

      return hasDetailChanges || hasTransactionChanges;
    });
  }, [transactions]);

  // 获取变更统计
  const getChangesSummary = useCallback(() => {
    let newCount = 0;
    let modifiedCount = 0;
    let deletedCount = 0;

    transactions.forEach((transaction) => {
      // 统计明细变更
      transaction.details.forEach((detail) => {
        switch (detail.clientStatus) {
          case CLIENT_STATUS.NEW:
            newCount++;
            break;
          case CLIENT_STATUS.MODIFIED:
            modifiedCount++;
            break;
          case CLIENT_STATUS.DELETED:
            deletedCount++;
            break;
          default:
            break;
        }
      });

      // 统计事务本身的变更
      if (typeof transaction.id === "number" && transaction.originalData) {
        const hasTransactionChanges = transaction.status !== transaction.originalData.status || 
          transaction.remarks !== transaction.originalData.remarks;
        if (hasTransactionChanges) {
          modifiedCount++; // 事务变更算作修改
        }
      }
    });

    return {
      new: newCount,
      modified: modifiedCount,
      deleted: deletedCount,
      total: newCount + modifiedCount + deletedCount,
    };
  }, [transactions]);

  // 找出没有任何（未删除）明细的事务，用于提示
  const getTransactionsMissingDetails = useCallback(() => {
    return transactions.filter((t) => 
      (t.details || []).filter((d) => d.clientStatus !== CLIENT_STATUS.DELETED).length === 0
    ).map((t) => t.id);
  }, [transactions]);

  // 检查提交条件
  const canSubmit = useCallback(() => {
    if (workflowMode === "EDIT") {
      // 编辑模式：检查是否有变更
      return hasChanges();
    } else if (workflowMode === "MIXED") {
      // 混合模式：检查是否有变更或新增
      return hasChanges();
    } else if (workflowMode === "CREATE") {
      // 创建模式：检查是否有新增的明细（至少一条未删除的明细）
      const hasNewDetails = transactions.some((transaction) => 
        transaction.details.some((detail) => detail.clientStatus === CLIENT_STATUS.NEW)
      );
      return hasNewDetails;
    }

    return false;
  }, [workflowMode, hasChanges, transactions]);

  // 构建批量处理请求payload
  const buildBatchProcessPayload = useCallback(() => {
    const payload = {
      transaction_type: transactionType,
      creates: [],
      updates: [],
      deletes: [],
    };

    transactions.forEach((transaction) => {
      const newDetails = [];
      const modifiedDetails = [];
      const deletedDetailIds = [];

      transaction.details.forEach((detail) => {
        switch (detail.clientStatus) {
          case "new":
            const newDetailData = {
              contract_item_id: detail.contractItemId,
              product_name: detail.productName,
              supplier_name: detail.supplierName,
              unit_price: detail.unitPrice,
              currency: detail.currency,
              quantity: detail.quantity,
              unit: detail.unit,
              remarks: detail.remarks,
              type_specific_fields: detail.typeSpecificFields,
            };
            // 只有许可类型的明细才包含状态字段
            if (transactionType === "PERMIT_REQUEST") {
              newDetailData.status = detail.currentStatus;
            }
            newDetails.push(newDetailData);
            break;
          case "modified":
            const modifiedDetailData = {
              id: detail.id,
              contract_item_id: detail.contractItemId,
              product_name: detail.productName,
              supplier_name: detail.supplierName,
              unit_price: detail.unitPrice,
              currency: detail.currency,
              quantity: detail.quantity,
              unit: detail.unit,
              remarks: detail.remarks,
              type_specific_fields: detail.typeSpecificFields,
            };
            // 只有许可类型的明细才包含状态字段
            if (transactionType === "PERMIT_REQUEST") {
              modifiedDetailData.status = detail.currentStatus;
            }
            modifiedDetails.push(modifiedDetailData);
            break;
          case "deleted":
            if (typeof detail.id === "number") {
              deletedDetailIds.push(detail.id);
            }
            break;
          default:
            break;
        }
      });

      // 处理创建操作
      if (newDetails.length > 0) {
        // 创建事务时必须有供应商ID
        if (!selectedSupplier?.id) {
          throw new Error(`创建事务时必须选择供应商，航班: ${transaction.flightNumber}`);
        }

        // 查找对应的航班信息以创建快照
        const flightSnapshot = selectedFlights.find(f => f.id === transaction.flightId);

        payload.creates.push({
          transaction_id: typeof transaction.id === "number" ? transaction.id : null,
          flight_id: transaction.flightId,
          supplier_id: selectedSupplier.id, // 必须传递supplier_id
          details: newDetails,
          remarks: transaction.remarks,
          // [关键修复] 添加事务状态字段
          transaction_status: transaction.status || 'PENDING',
          additional_data: {
            activeAirports,
            supplierId: selectedSupplier.id,
            supplierName: selectedSupplier.name,
            // [关键修复] 添加航班快照
            flight_snapshot: flightSnapshot ? { ...flightSnapshot, snapshot_time: new Date().toISOString() } : null,
          },
        });
      }

      // 处理更新操作
      const hasModifiedDetails = modifiedDetails.length > 0;
      const hasTransactionChanges =
        typeof transaction.id === "number" &&
        // 检查事务本身是否有变更
        ((transaction.originalData && transaction.status !== transaction.originalData.status) ||
         (transaction.originalData && transaction.remarks !== transaction.originalData.remarks));

      if ((hasModifiedDetails || hasTransactionChanges) && typeof transaction.id === "number") {
        // 更新操作时必须有供应商ID
        if (!selectedSupplier?.id) {
          throw new Error(`更新事务时必须选择供应商，航班: ${transaction.flightNumber}`);
        }

        const updatePayload = {
          transaction_id: transaction.id,
          supplier_id: selectedSupplier.id, // 覆盖式设置supplier_id，兼容历史数据
          details: modifiedDetails,
          remarks: transaction.remarks,
          additional_data: {
            activeAirports,
            supplierId: selectedSupplier.id,
            supplierName: selectedSupplier.name,
          },
        };

        // 如果事务状态有变更，添加事务状态字段
        if (hasTransactionChanges) {
          updatePayload.transaction_status = transaction.status;
          console.log("📝 事务状态变更:", {
            transactionId: transaction.id,
            oldStatus: transaction.originalData?.status,
            newStatus: transaction.status,
          });
        }

        payload.updates.push(updatePayload);
      }

      // 处理删除操作
      if (deletedDetailIds.length > 0 && typeof transaction.id === "number") {
        // 删除操作时必须有供应商ID
        if (!selectedSupplier?.id) {
          throw new Error(`删除事务明细时必须选择供应商，航班: ${transaction.flightNumber}`);
        }

        payload.deletes.push({
          transaction_id: transaction.id,
          supplier_id: selectedSupplier.id, // 传递supplier_id
          detail_ids: deletedDetailIds,
          additional_data: {
            activeAirports,
            supplierId: selectedSupplier.id,
            supplierName: selectedSupplier.name,
          },
        });
      }
    });

    return payload;
  }, [transactions, transactionType, activeAirports, selectedSupplier]);

  // 提交事务
  const submitTransactions = useCallback(async (onSuccess) => {
    if (!canSubmit()) {
      // 增强型提示：具体提示哪些事务缺少明细
      const missing = transactions.filter((t) =>
        (t.details || []).filter((d) => d.clientStatus !== "deleted").length === 0
      ).map((t) => t.flightNumber || t.id);

      if (missing.length) {
        message.error(`以下航班的事务缺少明细（产品），请先添加：${missing.join("，")}`);
      } else {
        message.error("提交条件不满足，请检查必填项");
      }
      return { success: false };
    }

    try {
      const payload = buildBatchProcessPayload();
      console.log("🚀 批量处理请求payload:", payload);

      const result = await batchProcessTransactions(payload);

      if (result.success) {
        const changesSummary = getChangesSummary();
        // 构建动态的成功消息，只显示有变更的类别
        const buildSuccessMessage = () => {
          if (result.message) {
            return result.message;
          }

          const parts = [];
          if (result.created_count > 0) {
            parts.push(`创建: ${result.created_count}`);
          }
          if (result.updated_count > 0) {
            parts.push(`更新: ${result.updated_count}`);
          }
          if (result.deleted_count > 0) {
            parts.push(`删除: ${result.deleted_count}`);
          }

          return parts.length > 0 ? `批量处理成功！${parts.join(", ")}` : "批量处理完成！";
        };

        message.success(
          <div>
            <div>{buildSuccessMessage()}</div>
          </div>,
          6, // 延长显示时间
        );

        if (onSuccess) {
          onSuccess({
            ...result,
            changesSummary,
          });
        }

        return { success: true, result, changesSummary };
      } else {
        const errText = result.message || (result.errors && result.errors.length ? result.errors.join(", ") : "未知错误");
        message.error(`批量处理失败: ${errText}`);
        return { success: false, error: errText };
      }
    } catch (error) {
      console.error("提交失败:", error);
      const errorMessage = "提交失败: " + (error.message || "未知错误");
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [canSubmit, transactions, buildBatchProcessPayload, batchProcessTransactions, getChangesSummary]);

  return {
    // 明细操作
    addDetailWithPermitType,
    updateDetail,
    deleteDetail,
    restoreDetail,
    resetDetail,

    // 事务操作
    updateTransaction,
    createTransactionShells,

    // 计算属性和辅助方法
    hasChanges,
    getChangesSummary,
    getTransactionsMissingDetails,
    canSubmit,

    // 批量处理
    buildBatchProcessPayload,
    submitTransactions
  };
};
